import React from "react"
import styled from "styled-components"
import { BLOCKS } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { motion } from "framer-motion"
import { useForm } from "react-hook-form"

const OurHistorySection = styled.section`
  display: flex;
  justify-content: center;
  padding-top: 58px;
  padding-bottom: 80px;
`

const Timeline = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  /* overflow-x: scroll; */
  margin-bottom: 50px;
  width: 100%;
  padding: 0 64px;
  cursor: pointer;
  h2 {
    font-family: "Tinos", serif;
    font-style: italic;
    font-weight: bold;
    font-size: 22px;
    line-height: 25px;
    color: #989085;
    position: relative;

    :after {
      content: "";
      display: block;
      position: absolute;
      border-bottom: 1px solid #cbc5bd;
      width: 56px;
      top: 30px;
    }
  }

  > *:not(:last-child) {
    margin-right: 32px;
  }
  @media only screen and (max-width: 600px) {
    padding: 0 8px;
    width: 100%;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: scroll;
    white-space: nowrap;
    height: auto;
    justify-content: space-between;
  }
`

type PeriodProps = {
  status: "active" | "inactive"
}
const Period = styled(motion.div)<PeriodProps>`
  margin-bottom: 32px;
  p.title {
    font-family: "Amasis", serif;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 23px;
    color: ${p => (p.status === "active" ? "#676152" : "#3e3120")};
    background-image: ${p =>
      p.status === "active"
        ? "linear-gradient(#f9f2ea, #f9f2ea)"
        : "linear-gradient(transparent, transparent)"};
    background-position: 0% 7px;
    background-repeat: no-repeat;
    background-size: 100% 15px;
  }

  p.date {
    font-family: "Tinos", serif;
    font-style: italic;
    font-weight: bold;
    font-size: 12px;
    line-height: 17px;
    color: #989085;
  }

  @media only screen and (max-width: 600px) {
    width: 250px;
  }
  @media only screen and (max-width: 500px) {
    p.title {
      font-size: 14px;
    }
  }
`

const WallOfText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: max-content;
  /* padding: 0 100px; */
  p.headline {
    font-family: "Amasis", serif;
    font-size: 26px;
    font-weight: 700;
    line-height: 33px;
    margin-bottom: 4px;
    width: max-content;
  }

  p.date {
    font-family: "Tinos", serif;
    font-size: 16px;
    font-style: italic;
    font-weight: 700;
    line-height: 18px;
    letter-spacing: 0em;
    text-align: left;
    margin-bottom: 8px;
    color: #989085;
    width: max-content;
  }

  div.text {
    display: flex;
    flex-wrap: wrap;
    /* justify-content: space-between; */
    /* width: 100%; */

    > div {
      display: flex;
      flex-direction: column;
    }
    p.text-wall {
      font-family: Cabin;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      max-width: 510px;
      width: 100%;

      &:not(:last-child) {
        margin-bottom: 4px;
      }
    }

    .first-column {
      margin-right: 20px;
    }
    @media only screen and (max-width: 1150px) {
      p.text-wall {
        max-width: 450px;
      }
    }
    @media only screen and (max-width: 995px) {
      p.text-wall {
        max-width: 380px;
      }
    }
    @media only screen and (max-width: 850px) {
      p.text-wall {
        max-width: 350px;
      }
    }
    @media only screen and (max-width: 768px) {
      p.text-wall {
        max-width: 100%;
      }
    }
  }
  @media only screen and (max-width: 768px) {
    width: 80%;
  }
`

const CheatWrapper = styled.div`
  max-width: 1300px;
  width: 100%;
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`

const HEIGHTSTODAY = "heightsToday"
const FIFTYYEARSHENCE = "50YearsHence"
const POETIZATIONANDFILIPINIZATION = "poetizationAndFilipinization"
const POLITICALSHIFT = "aPloticalShift"
const BEFOREHEIGHTS = "beforeHeights"

type SelectFormArguments =
  | typeof HEIGHTSTODAY
  | typeof FIFTYYEARSHENCE
  | typeof POETIZATIONANDFILIPINIZATION
  | typeof POLITICALSHIFT
  | typeof BEFOREHEIGHTS

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className="text-wall">{children}</p>
    ),
  },
  renderMark: {},
}
const OurHistory: React.FC<{
  heightsToday: string
  heightsTodayDate: string
  heightsTodayContentColumnA: string
  heightsTodayContentColumnB: string
  yearsHence: string
  yearsHenceDate: string
  yearsHenceContentColumnA: string
  yearsHenceContentColumnB: string
  poetizationAndFilipinization: string
  poetizationAndFilipinizationDate: string
  poetizationAndFilipinizationContentColumnA: string
  poetizationAndFilipinizationContentColumnB: string
  aPoliticalShift: string
  aPoliticalShiftDate: string
  aPoliticalShiftContentColumnA: string
  aPoliticalShiftContentColumnB: string
  beforeHeights: string
  beforeHeightsDate: string
  beforeHeightsContentColumnA: string
  beforeHeightsContentColumnB: string
}> = function ({
  heightsToday,
  heightsTodayDate,
  heightsTodayContentColumnA,
  heightsTodayContentColumnB,
  yearsHence,
  yearsHenceDate,
  yearsHenceContentColumnA,
  yearsHenceContentColumnB,
  poetizationAndFilipinization,
  poetizationAndFilipinizationDate,
  poetizationAndFilipinizationContentColumnA,
  poetizationAndFilipinizationContentColumnB,
  aPoliticalShift,
  aPoliticalShiftDate,
  aPoliticalShiftContentColumnA,
  aPoliticalShiftContentColumnB,
  beforeHeights,
  beforeHeightsDate,
  beforeHeightsContentColumnA,
  beforeHeightsContentColumnB,
}) {
  const [selected, setSelected] = React.useState(HEIGHTSTODAY)

  function setSelectedForm(form: SelectFormArguments) {
    setSelected(form)
  }
  return (
    <OurHistorySection>
      <CheatWrapper>
        <Main>
          <Timeline>
            <h2>Our History</h2>
            <Period
              onClick={() => setSelectedForm(HEIGHTSTODAY)}
              status={selected === "HEIGHTSTODAY" ? "active" : "inactive"}
            >
              <p className="title">{heightsToday}</p>
              <p className="date">{heightsTodayDate}</p>
            </Period>
            <Period
              onClick={() => setSelectedForm(FIFTYYEARSHENCE)}
              status={selected === "FIFTYYEARSHENCE" ? "active" : "inactive"}
            >
              <p className="title">{yearsHence}</p>
              <p className="date">{yearsHenceDate}</p>
            </Period>
            <Period
              onClick={() => setSelectedForm(POETIZATIONANDFILIPINIZATION)}
              status={
                selected === "POETIZATIONANDFILIPINIZATION"
                  ? "active"
                  : "inactive"
              }
            >
              <p className="title">{poetizationAndFilipinization}</p>
              <p className="date">{poetizationAndFilipinizationDate}</p>
            </Period>
            <Period
              onClick={() => setSelectedForm(POLITICALSHIFT)}
              status={selected === "POLITICALSHIFT" ? "active" : "inactive"}
            >
              <p className="title">{aPoliticalShift}</p>
              <p className="date">{aPoliticalShiftDate}</p>
            </Period>
            <Period
              onClick={() => setSelectedForm(BEFOREHEIGHTS)}
              status={selected === "BEFOREHEIGHTS" ? "active" : "inactive"}
            >
              <p className="title">{beforeHeights} </p>
              <p className="date">{beforeHeightsDate}</p>
            </Period>
          </Timeline>
          {selected === HEIGHTSTODAY ? (
            <WallOfText
              key={HEIGHTSTODAY}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
            >
              <p className="headline">{heightsToday}</p>
              <p className="date">{heightsTodayDate}</p>

              <div className="text">
                <div className="first-column">
                  {/* @ts-ignore */}
                  {renderRichText(heightsTodayContentColumnA, options)}
                </div>
                <div className="second-column">
                  {/* @ts-ignore */}
                  {renderRichText(heightsTodayContentColumnB, options)}
                </div>
              </div>
            </WallOfText>
          ) : null}
          {selected === FIFTYYEARSHENCE ? (
            <WallOfText
              key={FIFTYYEARSHENCE}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
            >
              <p className="headline">{yearsHence}</p>
              <p className="date">{yearsHenceDate}</p>

              <div className="text">
                <div className="first-column">
                  {/* @ts-ignore */}
                  {renderRichText(yearsHenceContentColumnA, options)}
                </div>
                <div className="second-column">
                  {/* @ts-ignore */}
                  {renderRichText(yearsHenceContentColumnB, options)}
                </div>
              </div>
            </WallOfText>
          ) : null}
          {selected === POETIZATIONANDFILIPINIZATION ? (
            <WallOfText
              key={POETIZATIONANDFILIPINIZATION}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
            >
              <p className="headline">{poetizationAndFilipinization}</p>
              <p className="date">{poetizationAndFilipinizationDate}</p>

              <div className="text">
                <div className="first-column">
                  {/* @ts-ignore */}
                  {renderRichText(
                    poetizationAndFilipinizationContentColumnA,
                    options
                  )}
                </div>
                <div className="second-column">
                  {/* @ts-ignore */}
                  {renderRichText(
                    poetizationAndFilipinizationContentColumnB,
                    options
                  )}
                </div>
              </div>
            </WallOfText>
          ) : null}
          {selected === POLITICALSHIFT ? (
            <WallOfText
              key={POLITICALSHIFT}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
            >
              <p className="headline">{aPoliticalShift}</p>
              <p className="date">{aPoliticalShiftDate}</p>

              <div className="text">
                <div className="first-column">
                  {/* @ts-ignore */}
                  {renderRichText(aPoliticalShiftContentColumnA, options)}
                </div>
                <div className="second-column">
                  {/* @ts-ignore */}
                  {renderRichText(aPoliticalShiftContentColumnB, options)}
                </div>
              </div>
            </WallOfText>
          ) : null}
          {selected === BEFOREHEIGHTS ? (
            <WallOfText
              key={BEFOREHEIGHTS}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
            >
              <p className="headline">{beforeHeights}</p>
              <p className="date">{beforeHeightsDate}</p>

              <div className="text">
                <div className="first-column">
                  {/* @ts-ignore */}
                  {renderRichText(beforeHeightsContentColumnA, options)}
                </div>
                <div className="second-column">
                  {/* @ts-ignore */}
                  {renderRichText(beforeHeightsContentColumnB, options)}
                </div>
              </div>
            </WallOfText>
          ) : null}
        </Main>
      </CheatWrapper>
    </OurHistorySection>
  )
}

export default OurHistory
